import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { http, formatApiErrorDetail } from "@/lib/api";
import { Award, CheckCircle2, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";

export default function FormationDetailPage() {
  const { slug } = useParams();
  const [f, setF] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { http.get(`/formations/${slug}`).then((r) => setF(r.data)).catch(() => setF(false)); }, [slug]);

  const enroll = async () => {
    if (!user || user === false) { navigate("/connexion?next=/formations/" + slug); return; }
    setEnrolling(true);
    try {
      await http.post(`/formations/${slug}/enroll`);
      toast.success("Formation ajoutée à votre espace");
      navigate(`/espace/formation/${slug}`);
    } catch (e) {
      toast.error("Erreur", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setEnrolling(false); }
  };

  if (f === null) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="animate-spin text-brand-500" /></div>;
  if (f === false) return <div className="container-x py-20 text-center">Formation introuvable. <Link to="/formations" className="link-inline">Retour au catalogue</Link></div>;

  return (
    <div>
      <Seo title={f.title} description={f.subtitle} />
      <section className="container-x pt-14 pb-10">
        <div className="grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-3">
            <Link to="/formations" className="text-sm text-slate-500 hover:text-slate-800">← Formations</Link>
            <div className="flex flex-wrap gap-2 mt-4">
              {f.tags?.map((t) => (<span key={t} className="text-[11px] font-medium uppercase tracking-wider text-brand-800 bg-brand-50 rounded-full px-2.5 py-1">{t}</span>))}
              <span className="text-[11px] font-bold uppercase tracking-widest bg-brand-500 text-white rounded-full px-2.5 py-1">Attestation incluse</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-4">{f.title}</h1>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">{f.subtitle}</p>
            <div className="mt-6 flex flex-wrap gap-6 text-sm text-slate-600">
              <span className="flex items-center gap-2"><Award size={16} strokeWidth={1.5} className="text-brand-600" />{f.level}</span>
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="card-soft overflow-hidden sticky top-24">
              <img src={f.thumbnail} alt={f.title} className="w-full aspect-[16/10] object-cover" />
              <div className="p-6">
                <div className="flex items-baseline justify-between">
                  <div className="font-display text-xl font-bold text-slate-900">Devis personnalisé</div>
                </div>
                <button data-testid="enroll-btn" onClick={enroll} disabled={enrolling} className="btn-primary w-full mt-5 disabled:opacity-70">
                  {enrolling ? <Loader2 size={16} className="animate-spin" /> : <>Commander la formation</>}
                </button>
                <Link to="/offre#devis" data-testid="devis-link" className="btn-outline w-full mt-3 text-sm">
                  Contactez-nous pour un devis personnalisé
                </Link>
                <p className="text-xs text-slate-500 text-center mt-3">Commande depuis votre profil · Réponse sous 48h avec un devis personnalisé</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="container-x py-14 grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-3">
          <div className="eyebrow mb-3">Ce que vous allez y gagner</div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Des bénéfices concrets pour votre quotidien</h2>
          <ul className="mt-6 space-y-4">
            {f.benefits?.map((b) => (
              <li key={b} className="flex gap-3">
                <CheckCircle2 className="text-emerald-500 shrink-0 mt-0.5" size={20} strokeWidth={1.5} />
                <span className="text-slate-700 leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        </div>
        <aside className="lg:col-span-2">
          <div className="card-soft p-7">
            <div className="grid place-items-center h-12 w-12 rounded-2xl bg-brand-100 text-brand-700 mb-4"><Lock strokeWidth={1.5} /></div>
            <h3 className="font-display font-bold text-lg text-slate-900">Espace membre inclus</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">Suivez votre progression, reprenez où vous vous étiez arrêté(e), échangez avec la communauté professionnelle.</p>
          </div>
          <div className="card-soft p-7 mt-4">
            <div className="grid place-items-center h-12 w-12 rounded-2xl bg-emerald-100 text-emerald-700 mb-4"><Award strokeWidth={1.5} /></div>
            <h3 className="font-display font-bold text-lg text-slate-900">Attestation de participation</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">Remise en fin de session, à télécharger depuis votre espace membre et à joindre à votre dossier professionnel.</p>
          </div>
        </aside>
      </section>
    </div>
  );
}
